import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSelectedBankAccountId } from '@monetr/interface/hooks/useSelectedBankAccountId';
import type Spending from '@monetr/interface/models/Spending';
import request from '@monetr/interface/util/request';

export function useRemoveSpending(): (_spendingId: string) => Promise<unknown> {
  const queryClient = useQueryClient();
  const selectedBankAccountId = useSelectedBankAccountId();

  async function removeSpending(spendingId: string): Promise<string> {
    return request({
      method: 'DELETE',
      url: `/api/bank_accounts/${selectedBankAccountId}/spending/${spendingId}`,
    }).then(() => spendingId);
  }

  const mutation = useMutation({
    mutationFn: removeSpending,
    onSuccess: (removedSpendingId: string) =>
      Promise.all([
        queryClient.setQueryData(
          [`/api/bank_accounts/${selectedBankAccountId}/spending`],
          (previous: Array<Partial<Spending>>) => previous.filter(item => item.spendingId !== removedSpendingId),
        ),
        queryClient.removeQueries({
          queryKey: [`/api/bank_accounts/${selectedBankAccountId}/spending/${removedSpendingId}`],
        }),
        queryClient.invalidateQueries({ queryKey: [`/api/bank_accounts/${selectedBankAccountId}/balances`] }),
        queryClient.invalidateQueries({ queryKey: [`/api/bank_accounts/${selectedBankAccountId}/forecast`] }),
        queryClient.invalidateQueries({
          queryKey: [`/api/bank_accounts/${selectedBankAccountId}/forecast/next_funding`],
        }),
        queryClient.invalidateQueries({ queryKey: [`/api/bank_accounts/${selectedBankAccountId}/transactions`] }),
      ]),
  });

  return mutation.mutateAsync;
}
